/**
 * useLiquiditaetsplanung (#1315) — the single twin-API interface point
 * for the Liquiditätsplanung app (APPS_LAYER convention: only the hook
 * touches twinService). Reads the four ``liquiditaet_*`` fact types for
 * the session tenant, narrows them, and runs the pure
 * ``computeBalanceSeries`` engine. Returning the raw transactions plus
 * the derived series is what lets subtask 05 recompute instantly on an
 * optimistically-updated cache — no server round-trip for the curve.
 */
import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  twinService,
  type DerivedFactListResponse,
  type EntityListResponse,
} from '../services/twinService';
import { fetchAllPagesTracked } from '../services/paging';
import {
  CASH_FLOW_AGGREGATE_EXCLUDED_STATUS,
  CASH_FLOW_AGGREGATE_GROUP_BY,
  aggregateGroupsToCashFlows,
} from '../pages/apps/liquiditaetsplanung/cashFlowAggregate';
import {
  accountToConfig,
  contractedRecordsFrom,
  engineRecordsFrom,
} from '../pages/apps/liquiditaetsplanung/cashFlowProjection';
import {
  LEGACY_ALIAS_ACCOUNT_ID,
  accountsFromEntities,
  isSyntheticAccountRow,
  revenueBucketAccountIds,
  type LiquidityAccount,
} from '../pages/apps/liquiditaetsplanung/accounts';
import {
  partitionEngineRecords,
  orphanedFlowStats,
  statementsForAccount,
} from '../pages/apps/liquiditaetsplanung/accountPartition';
import { sumBalanceSeries } from '../pages/apps/liquiditaetsplanung/aggregateSeries';
import {
  applyOptimisticPlanned,
  optimisticId,
  reconcilePlannedId,
  type PlannedTransactionInput,
} from '../pages/apps/liquiditaetsplanung/optimisticPlanned';
import {
  toBank,
  toConfig,
  toPlanned,
  toRecord,
  toStatement,
} from '../pages/apps/liquiditaetsplanung/liquiditaetMappers';
import {
  bankRecordsFrom,
  groupRecords,
  mergeRecords,
  planSupersededByPayed,
  plannedToRecord,
  recordToBankFact,
  recordToPlanned,
} from '../pages/apps/liquiditaetsplanung/transactions';
import { computeBalanceSeries } from '../pages/apps/liquiditaetsplanung/computeBalanceSeries';
import { todayIso } from '../utils/today';
import type {
  BalanceSeriesResult,
  BankTransaction,
  LiquiditaetConfig,
  LiquiditaetRecord,
  PlannedTransaction,
  StatementBalance,
  Transaction,
} from '../pages/apps/liquiditaetsplanung/types';

// The backend caps `limit` at 1000 per page. A farm-year in the lifecycle-records
// model is one planned + one payed record per line-month — low thousands of rows —
// so fetch ALL pages (silently truncating at one page corrupts the projection /
// verdict). MAX_PAGES is a runaway guard (10×1000 = 10k records).
const PAGE_LIMIT = 1000;
const MAX_PAGES = 10;

export const liquiditaetKeys = {
  all: ['liquiditaetsplanung'] as const,
  facts: (factType: string) => [...liquiditaetKeys.all, factType] as const,
  entities: (entityType: string) => [...liquiditaetKeys.all, 'entity', entityType] as const,
};

/**
 * A page-walked feed plus whether it stopped short (#2611). ``truncated`` is
 * carried all the way to the page so a partial read is never rendered as a
 * complete Bestand.
 */
interface TrackedFacts extends DerivedFactListResponse {
  truncated: boolean;
}
interface TrackedEntities {
  rows: Record<string, unknown>[];
  truncated: boolean;
  /** The rows came from the server-side daily aggregate, not a page walk. */
  aggregated: boolean;
  /** Amounts the server could not read as money (aggregate path only). */
  sumSkipped?: number;
}

async function fetchAllFacts(factType: string): Promise<TrackedFacts> {
  const { items, truncated } = await fetchAllPagesTracked(
    async (page) => {
      const res = await twinService.queryDerivedFacts({ fact_type: factType, page, limit: PAGE_LIMIT });
      return { items: res.derived_facts ?? [], hasMore: res.has_more };
    },
    { pageCap: MAX_PAGES },
  );
  return { derived_facts: items, total: items.length, page: 1, limit: items.length, has_more: false, truncated };
}

function useFactQuery(factType: string) {
  return useQuery({
    queryKey: liquiditaetKeys.facts(factType),
    queryFn: () => fetchAllFacts(factType),
    staleTime: 30_000,
  });
}

async function fetchAllEntities(
  entityType: string,
  body: { attr_in?: Record<string, string[]> } = {},
): Promise<TrackedEntities> {
  const { items, truncated } = await fetchAllPagesTracked(
    async (page) => {
      const res: EntityListResponse = await twinService.queryEntities(entityType, {
        ...body,
        page,
        limit: PAGE_LIMIT,
      });
      return { items: res.entities ?? [], hasMore: res.has_more ?? false };
    },
    { pageCap: MAX_PAGES },
  );
  return { rows: items, truncated, aggregated: false };
}

function useEntityQuery(entityType: string) {
  return useQuery({
    queryKey: liquiditaetKeys.entities(entityType),
    queryFn: () => fetchAllEntities(entityType),
    staleTime: 30_000,
  });
}

/**
 * The ``cash_flow`` feed (#2611): ONE server-side daily aggregate for the bulk
 * of the movements + the ``planned`` flows raw.
 *
 * A tenant's flow count grows monthly and unboundedly (bank imports); the raw
 * page walk was O(rows) with a hard 10k ceiling it silently hit. The aggregate
 * is O(account x day) — WF's 19,344 rows become ~1.8k buckets — so the ceiling
 * stops being reachable in practice, and the fail-loud flag covers the rest.
 *
 * Falls back to the raw page walk if the aggregate endpoint is unavailable
 * (a backend older than this deploy): the app must keep working through a
 * rolling deploy, just with the pre-#2611 truncation behaviour — which is now
 * at least REPORTED.
 */
async function fetchCashFlowFeed(): Promise<TrackedEntities> {
  // The try wraps ONLY the aggregate call. Wrapping the whole block would also
  // swallow a failure of the small ``planned`` read and answer it with a full
  // row walk, and would hide a genuine defect in the aggregate behind a silent
  // downgrade — the exact posture this issue exists to remove.
  let aggregate: Awaited<ReturnType<typeof twinService.aggregateEntities>> | null = null;
  try {
    aggregate = await twinService.aggregateEntities('cash_flow', {
      groupBy: CASH_FLOW_AGGREGATE_GROUP_BY,
      sumAttribute: 'amount',
      attrNotIn: { status: CASH_FLOW_AGGREGATE_EXCLUDED_STATUS },
    });
  } catch (err) {
    // Expected only against a backend older than this deploy; anything else is
    // a real defect, so say so rather than degrading in silence.
    console.warn('cash_flow aggregate unavailable; falling back to the paged read', err);
    return fetchAllEntities('cash_flow');
  }
  const planned = await fetchAllEntities('cash_flow', {
    attr_in: { status: CASH_FLOW_AGGREGATE_EXCLUDED_STATUS },
  });
  const items = aggregate.items ?? [];
  return {
    rows: [...aggregateGroupsToCashFlows(items), ...planned.rows],
    // An unreadable truncation signal means "assume incomplete".
    truncated: (aggregate.truncated ?? true) || planned.truncated,
    aggregated: true,
    // Amounts the server could not read as money. They contribute 0 to their
    // bucket, so without this a bucket of unreadable amounts would render as a
    // confident 0 € delta — a partial sum passing as a complete one.
    sumSkipped: items.reduce((n, it) => n + (it.sum_skipped ?? 0), 0),
  };
}

function useCashFlowQuery() {
  return useQuery({
    queryKey: liquiditaetKeys.entities('cash_flow'),
    queryFn: fetchCashFlowFeed,
    staleTime: 30_000,
  });
}

export interface UseLiquiditaetsplanungResult {
  config: LiquiditaetConfig | null;
  planned: PlannedTransaction[];
  bankFacts: BankTransaction[];
  statements: StatementBalance[];
  /** The unified lifecycle records (native + legacy compat fold) — the source the
   *  grouped ``transactions`` come from. Exposed for the category-grouped budget
   *  view (#1617), which folds the SAME records by ``ledger_category``. */
  records: LiquiditaetRecord[];
  /** Grouped lifecycle transactions (transaction-records) — the ledger source. */
  transactions: Transaction[];
  series: BalanceSeriesResult;
  availableMonths: string[];
  /**
   * The tenant's bank accounts (#2005). Empty or a single entry for a
   * single-account tenant; N entries for a multi-bank tenant. The page renders
   * the account selector ONLY when ``length > 1`` (D4: N ≤ 1 → selector absent,
   * render-identical). The per-tenant roll-up sentinel is filtered out upstream.
   */
  accounts: LiquidityAccount[];
  /**
   * Per-account balance series (each anchored to its OWN opening_balance/limit) —
   * present ONLY for a multi-account tenant (N > 1). Drives the per-account chart
   * lines + the segment Ampel dots (D3: verdict.level per account).
   */
  accountSeries?: LiquidityAccountView[];
  /**
   * The "Alle Konten" aggregate = Σ of the per-account daily series (D1) — present
   * ONLY for N > 1. For N > 1 the top-level ``series`` aliases this total so
   * existing consumers keep working.
   */
  totalSeries?: BalanceSeriesResult;
  /**
   * The tenant HAS ``financial_account`` rows but not one real Konto among them —
   * every row is a synthetic revenue bucket (#2664). The whole surface is then
   * legitimately empty, and the page explains WHY (no bank account connected yet)
   * rather than letting the neutral state read as "this farm has no plan".
   *
   * False for a tenant with zero ``financial_account`` rows: that is the legacy
   * fact-fallback path, which still renders a real curve off ``liquiditaet_config``.
   */
  noBankAccountConnected: boolean;
  /**
   * Feed completeness (#2611). When ``truncated`` is set, at least one read
   * behind this result stopped at its page guard — so the Bestand, the Ist
   * horizon and the Ampel verdict are derived from a PARTIAL view and the page
   * must say so instead of rendering a confidently wrong number.
   */
  dataQuality: LiquidityDataQuality;
  isLoading: boolean;
  error: Error | null;
}

export interface LiquidityDataQuality {
  truncated: boolean;
  /** Which feeds stopped short — surfaced so a support call can name them. */
  truncatedSources: string[];
  /**
   * HOF-135 §1.6 — does a truncated feed actually reach the BALANCE this page renders?
   *
   * ``truncated`` is an OR across all seven reads, which is the right answer to "is any
   * part of this page incomplete?" and the wrong one to put above a euro figure. On the
   * entity substrate the balance comes from ``cash_flow`` through the server-side daily
   * aggregate, which is not page-walked; the 10k cap that fires on WF hits
   * ``liquiditaet_bank_transaction`` (14.946 facts → 10.000 read), and those facts feed
   * the LEDGER, not the curve. Rendered over the balance it said "this curve is missing
   * bookings", which is the one thing it did not mean — and a caveat that overstates the
   * problem teaches the farmer to discount the number when it is right (HOF-97 §1: Tanja
   * opened that issue because she could not derive our totals).
   *
   * Which feeds count is substrate-dependent, and the substrate is knowable exactly:
   * ``engineRecordsFrom`` returns the FACT records only when ``cashFlows.length === 0``.
   * So with flows present the bank facts are provably not in the curve, and with no flows
   * they provably are. Everything else — config, planned, statements, accounts — anchors
   * or moves the curve under both substrates and always counts.
   */
  balanceTruncated: boolean;
  /**
   * HOF-135 §1.6 — the mirror of ``balanceTruncated`` for the Ledger/Kategorien list:
   * the record/planned/bank fact families plus the contracted ``cash_flow`` fold. A
   * truncated ``financial_account`` or ``liquiditaet_statement_balance`` read leaves
   * that list complete, so it must not raise a notice over it.
   */
  ledgerTruncated: boolean;
  /**
   * HOF-135 §1.4 — ``cash_flow`` rows referencing an account that no longer exists,
   * and the ids they reference. Excludes the #2664 revenue buckets, which are absent
   * from the curve by design rather than by deletion. See ``orphanedFlowStats``.
   */
  orphanedFlowCount: number;
  orphanedAccountIds: string[];
  /** The balance history came from the server-side daily aggregate (#2611). */
  aggregated: boolean;
  /**
   * Cash-flow amounts the server could not read as money. They contribute 0 to
   * their day's delta, so a non-zero count means the curve is understated by an
   * unknown amount — a different defect from ``truncated``, and the banner
   * covers both.
   */
  sumSkipped: number;
}

/** One account's engine output (#2005): its own balance series. */
export interface LiquidityAccountView {
  account: LiquidityAccount;
  series: BalanceSeriesResult;
}

export function useLiquiditaetsplanung(): UseLiquiditaetsplanungResult {
  const configQuery = useFactQuery('liquiditaet_config');
  const recordQuery = useFactQuery('liquiditaet_record');
  const plannedQuery = useFactQuery('liquiditaet_planned_transaction');
  const bankQuery = useFactQuery('liquiditaet_bank_transaction');
  const statementQuery = useFactQuery('liquiditaet_statement_balance');
  // #1657 R1: the new entity substrate. After the in-place migration the balance
  // engine reads ``financial_account`` (anchor) + ``cash_flow`` (movements); the
  // legacy facts are retained (retain-and-dual-read) and still feed the rich ledger
  // detail below. Pre-migration tenants have no entities → the engine falls back to
  // the fact path, so this is render-identical in both states.
  const accountQuery = useEntityQuery('financial_account');
  const cashFlowQuery = useCashFlowQuery();

  // The fiscal-year start has no account home (a planning preference, §4.5) — it
  // stays read-of-record on the retained config fact.
  const factConfig = useMemo(() => {
    const row = configQuery.data?.derived_facts?.[0];
    return row ? toConfig(row) : null;
  }, [configQuery.data]);

  const account = useMemo(() => {
    // #2664 — drop synthetic revenue buckets BEFORE the anchored/fallback chain.
    // This pick used to read the RAW entity rows while the FE's own
    // ``accountsFromEntities`` filtered them correctly, so on a sole-bucket tenant
    // (wiberry-first, never uploaded a Kontoauszug) — or on a bucket + one
    // UNANCHORED real account, where ``anchored`` is empty — the ``fa-liquiditaet``
    // fallback below selected the BUCKET, scoped flows to it and folded synthetic
    // booth revenue into the curve. #2636 fixed the backend half; this is its
    // mirror, so FE and BE balances cannot diverge again on those two shapes.
    //
    // Filtering only, deliberately: ``accountsFromEntities`` would also RE-SORT A–Z,
    // and the pick below is documented as input-order-sensitive (``anchored[0]``).
    // Filtering alone is render-identical for every tenant that has a real Konto.
    // The shared ``isSyntheticAccountRow`` predicate also drops the roll-up sentinel,
    // so this pick and the Konto list agree on what is not an account — otherwise
    // excluding the bucket would merely move the wrong pick onto ``__tenant_total__``.
    const entities = (accountQuery.data?.rows ?? []).filter((e) => !isSyntheticAccountRow(e));
    // Prefer an ANCHORED account (#2076 B9): a tenant can legitimately carry the
    // anchor-less ``fa-liquiditaet`` revenue bucket (wiberry booth aggregates)
    // NEXT TO real bank accounts — blindly preferring the legacy alias then
    // blanks the curve even though 9 anchored Konten exist. The legacy alias
    // still wins when it is itself anchored (the migrated single-account tenant).
    const anchored = entities.filter((e) => e.opening_balance_date);
    return (
      anchored.find((e) => e.account_id === LEGACY_ALIAS_ACCOUNT_ID) ??
      anchored[0] ??
      entities.find((e) => e.account_id === LEGACY_ALIAS_ACCOUNT_ID) ??
      entities[0] ??
      null
    );
  }, [accountQuery.data]);

  // #2664 — the ids whose cash_flows leave with their filtered bucket. Only the
  // account-less fallback in ``engineRecords`` below needs them (see
  // ``revenueBucketAccountIds``); the multi-account fold scopes per surviving id.
  const bucketAccountIds = useMemo(
    () => revenueBucketAccountIds(accountQuery.data?.rows ?? []),
    [accountQuery.data],
  );

  const config = useMemo(() => {
    const wj = factConfig?.wj_start_month ?? 7;
    const entityConfig = account ? accountToConfig(account, wj) : null;
    // Prefer the migrated entity anchor; but a Kontoauszug-first financial_account
    // has no opening_balance_date, so fall back to the legacy config fact rather than
    // discard a valid anchor (the adapter-created account alone would blank the page).
    const parsed = entityConfig?.anchor_date ? entityConfig : factConfig;
    // A config with no usable anchor date is treated as missing, so the whole
    // surface shows the neutral state instead of crashing.
    return parsed?.anchor_date ? parsed : null;
  }, [account, factConfig]);

  // The unified record set: native ``liquiditaet_record`` facts + a compat fold
  // of any legacy ``planned``/``bank`` facts (a user-created/edited planned tx
  // writes a planned fact via the reused route, so it surfaces here as a planned
  // record). ``mergeRecords`` lets a user edit SUPERSEDE the native record it
  // shares a ``transaction_id`` with (the row moves, never double-counts).
  // Grouping by ``transaction_id`` yields the ledger source. This stays
  // fact-sourced so the ledger keeps the rich labels/qty the cash_flow atom drops.
  // #3107 — the fourth arm: `cash_flow`-sourced CONTRACTED records. A wibill
  // tenant's invoice expectations exist ONLY as entity attributes (nothing
  // writes `liquiditaet_record` for it), so without this fold `tx.contracted` is
  // never populated and both #3065 (`settled_at` suppressing the "did not
  // happen" grey) and #3026 (judging a payment against its CONTRACT when there
  // is no plan) are inert on the live ledger — every WF row reads `ungeplant`.
  //
  // Two-stage on purpose: the provenance dedupe must run against the fact
  // records that SURVIVE `mergeRecords`, not against its raw inputs, or a fact
  // record the fold drops would keep suppressing its entity counterpart and the
  // row would vanish from the ledger entirely. `mergeRecords` is a pure O(n)
  // concat, so folding twice inside this memo is cheaper and far clearer than
  // threading a half-folded set back out of it.
  const records = useMemo(() => {
    const fromRecords = (recordQuery.data?.derived_facts ?? []).map(toRecord);
    const fromPlanned = (plannedQuery.data?.derived_facts ?? []).map(toPlanned).map(plannedToRecord);
    // #3137 — set-level, not per-row: a settling credit is re-keyed onto the
    // expectation it pays, and `bankRecordsFrom` refuses that re-key when two
    // credits share one `planned_ref` (groupRecords' first-wins would drop the
    // second, losing a real credit) or when the credit's synthetic id ALREADY
    // anchors a record — a farmer-categorised credit is one half of an existing
    // pair (`liq-plan-<synthetic>`), and stranding its plan would both split the
    // row and double-count the money in the fact-path series. Un-settled facts
    // map exactly as before.
    const claimedIds = new Set([...fromRecords, ...fromPlanned].map((r) => r.transaction_id));
    const fromBank = bankRecordsFrom((bankQuery.data?.derived_facts ?? []).map(toBank), claimedIds);
    const factRecords = mergeRecords(fromRecords, fromPlanned, fromBank);
    const entityContracted = contractedRecordsFrom(cashFlowQuery.data?.rows ?? [], factRecords);
    return entityContracted.length === 0
      ? factRecords
      : mergeRecords(fromRecords, fromPlanned, fromBank, entityContracted);
  }, [recordQuery.data, plannedQuery.data, bankQuery.data, cashFlowQuery.data]);

  const transactions = useMemo(() => groupRecords(records), [records]);

  // The records the BALANCE ENGINE feeds on. Post-migration these come from the
  // ``cash_flow`` entities (the new substrate); pre-migration they fall back to the
  // fact-sourced ``records`` above. The projection is information-preserving on every
  // engine-relevant field, so the curve is byte-identical (the Nina parity gate).
  //
  // One exception keeps the live write loop intact: a USER-written planned transaction
  // stays the fact write-of-record (see ``engineRecordsFrom``), preserving the instant
  // optimistic curve recompute and a fresh create/edit before the server round-trip.
  // #2001 — the twin now models one ``cash_flow`` set per bank account, so feed
  // ONLY the picked account's flows into the single-account balance engine (was:
  // every account's flows summed against one anchor — the mixed-saldo defect).
  // Render-identical for a single-account tenant (all flows share its id); the
  // multi-account UX lands in #2005.
  // #2664 — a filtered-out revenue bucket takes its flows with it, mirroring the
  // backend's #2636 exclusion. Redundant whenever an account IS picked (the scope
  // below already keys on that account's id), but the account-less fallback takes
  // EVERY flow, so on a sole-bucket tenant it would otherwise re-admit exactly the
  // synthetic booth revenue the filter just excluded.
  //
  // HOF-135 §1.6 — hoisted out of ``engineRecords`` so the data-quality scoping below
  // can test the SAME list the engine consumes. It read the raw rows before, and the
  // two diverge on exactly the shapes that matter: on a #2664 sole-bucket tenant the
  // raw list is non-empty while ``scopedFlows`` is empty, so the engine falls back to
  // the FACT records while a raw-row test would call it the entity substrate — and
  // silently drop the bank feed from the balance's warning set.
  const scopedFlows = useMemo(() => {
    const rows = cashFlowQuery.data?.rows ?? [];
    const flows = bucketAccountIds.size
      ? rows.filter((cf) => typeof cf.account_id !== 'string' || !bucketAccountIds.has(cf.account_id))
      : rows;
    const accountId = account?.account_id;
    return accountId != null ? flows.filter((cf) => cf.account_id === accountId) : flows;
  }, [cashFlowQuery.data, account, bucketAccountIds]);

  const engineRecords = useMemo(
    () => engineRecordsFrom(scopedFlows, records),
    [scopedFlows, records],
  );

  // Engine feed: payed records are history (bank facts), planned records are the
  // projection. This is the "payed-else-planned" series rule — the engine itself
  // is unchanged (history = bank ≤ anchor, future = planned > anchor).
  //
  // A transaction that is already PAYED supersedes its own plan: drop the planned
  // record sharing a payed record's ``transaction_id`` from the projection feed
  // (#1653). The engine's istHorizon partition alone double-counts a plan dated
  // strictly AFTER its own earlier payment (payed ≤ horizon lands in the actual
  // region, plan > horizon lands in the projection), inflating the curve and
  // potentially masking an Engpass; excluding it at the feed keeps the actual the
  // single source of truth without touching the engine.
  //
  // Scheme-tolerant since #3171: the drop also matches the strip-normalised id
  // (``planSupersededByPayed``), because the engine feed re-keys a user plan to
  // its FULL ``liq-plan-…`` derived_fact_id while a settling credit's payed
  // record keys the bare entity scheme from ``stripCf(planned_ref)`` — the two
  // never string-match, so a #3152-grouped, future-re-dated plan would
  // otherwise double-count once its expectation settles.
  const bankFacts = useMemo(
    () => engineRecords.filter((r) => r.record_role === 'payed').map(recordToBankFact),
    [engineRecords],
  );
  const planned = useMemo(() => {
    const payedTxIds = new Set(
      engineRecords.filter((r) => r.record_role === 'payed').map((r) => r.transaction_id),
    );
    return engineRecords
      .filter((r) => r.record_role === 'planned' && !planSupersededByPayed(r.transaction_id, payedTxIds))
      .map(recordToPlanned);
  }, [engineRecords]);
  const statements = useMemo(
    () => (statementQuery.data?.derived_facts ?? []).map(toStatement),
    [statementQuery.data],
  );

  // #2690 — the single-account run adopts the account-less legacy statements
  // (exactly the pre-#2690 behaviour) plus any anchored on the picked account. One
  // anchored elsewhere — a #2636 revenue bucket, another Konto — is not this
  // account's evidence and must not advance its Ist horizon.
  const singleStatements = useMemo(() => {
    const accountId = typeof account?.account_id === 'string' ? account.account_id : undefined;
    return statementsForAccount(statements, accountId, true);
  }, [statements, account]);

  const series = useMemo(
    () => computeBalanceSeries(config, bankFacts, singleStatements, planned, todayIso()),
    [config, bankFacts, singleStatements, planned],
  );

  // #2664 — a tenant that HAS financial_account rows but no real Konto among them,
  // AND whose surface is consequently empty. The page then says WHY it is empty
  // rather than letting the neutral state read as "this farm has no plan".
  //
  // Both halves are load-bearing. The row shape alone is not enough: a sole
  // ``fa-liquiditaet`` row can be BOTH anchored and a bucket (the backend's
  // ``project_config_to_account`` re-stamps the marker over a migrated anchor —
  // the upsert is a full attribute REPLACE — and the worker's
  // ``_ensure_legacy_liquidity_account`` patches it onto an already-anchored row).
  // The account is then filtered here, but ``config`` falls back to the retained
  // ``liquiditaet_config`` fact and the retained facts still feed the ledger, so the
  // page renders a real Kontostand and curve. Telling that farmer "you cannot see
  // any of this until you connect a bank account" would be a lie.
  //
  // Zero rows is likewise excluded: that is the legacy fact-fallback path, whose
  // curve renders fine — noise on a working page.
  //
  // Erring toward NOT showing the note is the safe direction: a missed note leaves
  // today's bare empty state, a wrong one contradicts what is on screen.
  const noBankAccountConnected = useMemo(() => {
    const rows = accountQuery.data?.rows ?? [];
    if (rows.length === 0 || !rows.every((e) => isSyntheticAccountRow(e))) return false;
    return config == null && records.length === 0 && series.dailySeries.length === 0;
  }, [accountQuery.data, config, records, series]);

  // ── #2005 account dimension ──────────────────────────────────────────
  // The tenant's bank accounts, alias-first, roll-up sentinel filtered. Always
  // derived (cheap); the page only surfaces a selector when there is more than one.
  const wjStartMonth = factConfig?.wj_start_month ?? 7;
  const accounts = useMemo(
    () => accountsFromEntities(accountQuery.data?.rows ?? [], wjStartMonth),
    [accountQuery.data, wjStartMonth],
  );

  // Multi-account (N > 1) ONLY: run the engine once PER account (its own flows
  // against its own anchor/limit — the mixed-saldo fix) with the account-less user
  // plans overlaid per D2. N ≤ 1 short-circuits to ``undefined`` so the single path
  // above is the literal render (D4). Memoised so the optimistic write loop stays
  // instant.
  const accountViews = useMemo<LiquidityAccountView[] | undefined>(() => {
    if (accounts.length <= 1) return undefined;
    const { byAccount } = partitionEngineRecords(cashFlowQuery.data?.rows ?? [], records, accounts);
    const today = todayIso();
    return accounts.map((account) => {
      const accRecords = byAccount.get(account.id) ?? [];
      const accBank = accRecords.filter((r) => r.record_role === 'payed').map(recordToBankFact);
      const payedTxIds = new Set(
        accRecords.filter((r) => r.record_role === 'payed').map((r) => r.transaction_id),
      );
      const accPlanned = accRecords
        .filter((r) => r.record_role === 'planned' && !planSupersededByPayed(r.transaction_id, payedTxIds))
        .map(recordToPlanned);
      // Prefer the account's own anchor; the legacy config-fact fallback is the
      // ALIAS account's alone (a non-alias Kontoauszug-first account with no anchor
      // falls to the synthesized default inside computeBalanceSeries).
      const preferred = account.config?.anchor_date
        ? account.config
        : account.isAlias
          ? factConfig
          : account.config;
      const accConfig = preferred?.anchor_date ? preferred : null;
      // #2690 — route each statement to the account its fact is ANCHORED on; the
      // alias additionally adopts the account-less legacy rows (the pre-#2690
      // ``account.isAlias ? statements : []``, preserved as the fallback). Before
      // this, a #2612 fact anchored on a minted account reached nothing — and on
      // WF, whose ``fa-liquiditaet`` alias is a #2636-filtered revenue bucket, NO
      // account satisfied ``isAlias``, so the statements reached nothing at all.
      const accStatements = statementsForAccount(statements, account.id, account.isAlias);
      return {
        account,
        // HOF-97 — stamp the Konto onto the series so ``sumBalanceSeries`` can say WHICH
        // account drifted. Mirror of ``liquiditaet_projection``'s ``acc_series["account_id"]``.
        series: {
          ...computeBalanceSeries(accConfig, accBank, accStatements, accPlanned, today),
          accountId: account.id,
        },
      };
    });
  }, [accounts, cashFlowQuery.data, records, factConfig, statements]);

  // "Alle Konten" = Σ of the per-account daily series (D1).
  const totalSeries = useMemo<BalanceSeriesResult | undefined>(
    () => (accountViews ? sumBalanceSeries(accountViews.map((v) => v.series)) : undefined),
    [accountViews],
  );

  // For N > 1 the top-level series aliases the total; N ≤ 1 keeps the
  // single-account series literally (parity gate).
  const outSeries = totalSeries ?? series;

  // Every month present in the (selected) series — history months + the 12-month
  // window — so the lens can navigate any month, not just funded ones.
  const availableMonths = useMemo(() => {
    const set = new Set<string>();
    for (const p of outSeries.dailySeries) set.add(p.date.slice(0, 7));
    return [...set].sort();
  }, [outSeries]);

  const queries = [
    configQuery,
    recordQuery,
    plannedQuery,
    bankQuery,
    statementQuery,
    accountQuery,
    cashFlowQuery,
  ];
  const isLoading = queries.some((q) => q.isLoading);
  const error = (queries.find((q) => q.error)?.error as Error | undefined) ?? null;

  // #2611 — feed completeness. Every read behind this result reports whether
  // it stopped at its page guard; the page renders a data-quality banner
  // instead of a silently wrong Bestand/verdict.
  const dataQuality = useMemo<LiquidityDataQuality>(() => {
    const sources: [string, boolean | undefined][] = [
      ['liquiditaet_config', configQuery.data?.truncated],
      ['liquiditaet_record', recordQuery.data?.truncated],
      ['liquiditaet_planned_transaction', plannedQuery.data?.truncated],
      ['liquiditaet_bank_transaction', bankQuery.data?.truncated],
      ['liquiditaet_statement_balance', statementQuery.data?.truncated],
      ['financial_account', accountQuery.data?.truncated],
      ['cash_flow', cashFlowQuery.data?.truncated],
    ];
    const truncatedSources = sources.filter(([, t]) => t === true).map(([name]) => name);
    const sumSkipped = cashFlowQuery.data?.sumSkipped ?? 0;

    // HOF-135 §1.6 — the balance-scoped subset.
    //
    // The substrate test is ``engineRecordsFrom``'s own (`cashFlows.length === 0`)
    // taken on ``scopedFlows``, the list the engine is actually handed — NOT the raw
    // rows, which diverge on a #2664 sole-bucket tenant, and not ``accounts`` or
    // ``aggregated``, which answer neighbouring questions and would drift from the fold
    // that decides which records reach the engine. The fact fallback is reachable only
    // on the single-account path: ``partitionEngineRecords`` scopes per account and
    // never substitutes the whole fact set for a flow-less Konto.
    const usesFactSubstrate = accounts.length <= 1 && scopedFlows.length === 0;
    const balanceFeeds = new Set([
      'liquiditaet_config',
      // User-written plans reach the projection under BOTH substrates —
      // ``engineRecordsFrom`` appends them and ``partitionEngineRecords`` overlays them
      // via ``userPlansFrom``. They arrive on the ``liquiditaet_record`` feed as well as
      // the planned one (both carry ``source: 'user'``), so a truncated record read
      // drops forecast money out of the curve and must always count.
      'liquiditaet_record',
      'liquiditaet_planned_transaction',
      'liquiditaet_statement_balance',
      'financial_account',
      // The movements. ``cash_flow`` counts under both substrates deliberately: a
      // truncated flow read is *why* the engine might see no flows at all, so excluding
      // it on the fact branch would let the failure hide itself.
      'cash_flow',
      ...(usesFactSubstrate ? ['liquiditaet_bank_transaction'] : []),
    ]);
    // ``sumSkipped`` is unreadable MONEY inside the aggregate buckets, so it understates
    // the curve itself — always a balance concern, never merely a ledger one.
    const balanceTruncated =
      sumSkipped > 0 || truncatedSources.some((name) => balanceFeeds.has(name));

    // HOF-135 §1.6 — the LEDGER's own subset, symmetric with the balance's. The list is
    // built from ``records`` (the record/planned/bank fact families) plus the contracted
    // ``cash_flow`` fold; a truncated ``financial_account`` or statement read leaves it
    // complete, and asserting otherwise would repeat this PR's own defect one surface
    // over — while naming an internal fact-type slug as the culprit.
    const ledgerFeeds = new Set([
      'liquiditaet_record',
      'liquiditaet_planned_transaction',
      'liquiditaet_bank_transaction',
      'cash_flow',
    ]);
    const ledgerTruncated =
      sumSkipped > 0 || truncatedSources.some((name) => ledgerFeeds.has(name));

    // A truncated ACCOUNT read makes every account past the page boundary look
    // tombstoned, which would report its live flows as orphans — the same
    // absence-by-design/absence-by-deletion conflation ``excludedAccountIds`` exists to
    // prevent, arriving through a different door. Report nothing rather than a number
    // built on a list we know is short.
    const accountsComplete = accountQuery.data?.truncated !== true;
    const { orphanedAccountIds, orphanedFlowCount } = accountsComplete
      ? orphanedFlowStats(cashFlowQuery.data?.rows ?? [], accounts, bucketAccountIds)
      : { orphanedAccountIds: [], orphanedFlowCount: 0 };

    return {
      balanceTruncated,
      ledgerTruncated,
      orphanedAccountIds,
      orphanedFlowCount,
      // Both conditions make the rendered figure understate reality, so both
      // raise the banner.
      truncated: truncatedSources.length > 0 || sumSkipped > 0,
      truncatedSources,
      aggregated: cashFlowQuery.data?.aggregated ?? false,
      sumSkipped,
    };
  }, [
    configQuery.data,
    recordQuery.data,
    plannedQuery.data,
    bankQuery.data,
    statementQuery.data,
    accountQuery.data,
    cashFlowQuery.data,
    accounts,
    bucketAccountIds,
    scopedFlows,
  ]);

  return {
    config,
    planned,
    bankFacts,
    statements,
    records,
    transactions,
    series: outSeries,
    availableMonths,
    accounts,
    accountSeries: accountViews,
    totalSeries,
    noBankAccountConnected,
    dataQuality,
    isLoading,
    error,
  };
}

/**
 * Create / edit / move one planned transaction. The cache is updated
 * optimistically so the curve + verdict recompute instantly (demo beat
 * 3); on error the snapshot is restored. For a create, the temp id is
 * minted in onMutate and swapped for the server id in onSuccess, so an
 * edit issued before the on-settle refetch targets the real row instead
 * of posting the temp id (which the backend would accept as a second
 * row). On settle the planned-fact query is invalidated to reconcile
 * against the canonical server state.
 */
export function useUpsertPlannedTransaction() {
  const queryClient = useQueryClient();
  const key = liquiditaetKeys.facts('liquiditaet_planned_transaction');

  return useMutation({
    mutationFn: (input: PlannedTransactionInput) =>
      twinService.upsertLiquiditaetPlannedTransaction(input),
    onMutate: async (input: PlannedTransactionInput) => {
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<DerivedFactListResponse>(key);
      // Mint the create's temp id here (not inside applyOptimisticPlanned) so
      // onSuccess can reconcile it to the server-assigned id. Edits already
      // carry a real id, so they need none.
      const tempId = input.derived_fact_id ? undefined : optimisticId();
      queryClient.setQueryData<DerivedFactListResponse>(key, (old) =>
        applyOptimisticPlanned(old, input, tempId),
      );
      return { previous, tempId };
    },
    onSuccess: (data, _input, context) => {
      const tempId = context?.tempId;
      if (tempId && data?.derived_fact_id) {
        queryClient.setQueryData<DerivedFactListResponse | undefined>(key, (old) =>
          reconcilePlannedId(old, tempId, data.derived_fact_id),
        );
      }
    },
    onError: (_err, _input, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(key, context.previous);
      } else {
        // No prior cache existed, so the optimistic update *created* the
        // entry. setQueryData(key, undefined) is a no-op in react-query, so
        // drop the query outright to clear the phantom row.
        queryClient.removeQueries({ queryKey: key });
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: key });
    },
  });
}
